import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Loader2 } from "lucide-react";

/**
 * Generic admin CRUD manager.
 * fields: [{ name, label, type: 'text'|'textarea'|'array'|'checkbox'|'image', help? }]
 */
export default function CrudTable({ title, endpoint, adminEndpoint, columns, fields, defaults, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', data}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (err) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing({ mode: "create", data: { ...defaults } });
  const startEdit = (row) => setEditing({ mode: "edit", data: { ...row } });
  const cancel = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing.data };
      for (const f of fields) {
        if (f.type === "array" && typeof payload[f.name] === "string") {
          payload[f.name] = payload[f.name].split("\n").map((s) => s.trim()).filter(Boolean);
        }
      }
      if (editing.mode === "create") {
        await api.post(adminEndpoint, payload);
        toast.success("Created.");
      } else {
        await api.put(`${adminEndpoint}/${editing.data.id}`, payload);
        toast.success("Updated.");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title || row.email || row.full_name}"?`)) return;
    try {
      await api.delete(`${adminEndpoint}/${row.id}`);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black text-[color:var(--mia-navy)] tracking-tight">{title}</h1>
          <p className="text-[color:var(--mia-muted)] mt-2">{items.length} record{items.length === 1 ? "" : "s"}</p>
        </div>
        {fields.length > 0 && (
          <button className="mia-btn mia-btn-secondary" onClick={startCreate} data-testid={`${testid}-create`}>
            <Plus className="w-4 h-4" /> New
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--mia-grey)]">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-4 py-3 font-semibold text-[color:var(--mia-navy)] uppercase text-xs tracking-widest">{c.label}</th>
                ))}
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[color:var(--mia-muted)]"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[color:var(--mia-muted)]">No records.</td></tr>
              ) : items.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-[color:var(--mia-navy)]">
                      {c.render ? c.render(row) : String(row[c.key] ?? "")}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {fields.length > 0 && (
                      <button className="text-[color:var(--mia-blue)] hover:text-[color:var(--mia-navy)] mr-3" onClick={() => startEdit(row)} data-testid={`edit-${row.id}`}>
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                    )}
                    <button className="text-red-500 hover:text-red-700" onClick={() => remove(row)} data-testid={`delete-${row.id}`}>
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && fields.length > 0 && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={cancel}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crud-modal">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-display text-2xl font-black text-[color:var(--mia-navy)]">{editing.mode === "create" ? "Create" : "Edit"} · {title}</h2>
              <button onClick={cancel} data-testid="crud-cancel"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="block text-xs uppercase tracking-widest text-[color:var(--mia-muted)] mb-2">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={f.rows || 4} value={editing.data[f.name] || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.value } })} className="w-full px-4 py-3 border border-slate-200 rounded-lg" data-testid={`field-${f.name}`} />
                  ) : f.type === "array" ? (
                    <textarea rows={f.rows || 3} value={Array.isArray(editing.data[f.name]) ? editing.data[f.name].join("\n") : (editing.data[f.name] || "")} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.value } })} placeholder="One item per line" className="w-full px-4 py-3 border border-slate-200 rounded-lg" data-testid={`field-${f.name}`} />
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!editing.data[f.name]} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.checked } })} data-testid={`field-${f.name}`} />
                      <span className="text-sm">{f.help || f.label}</span>
                    </label>
                  ) : (
                    <input type={f.type || "text"} value={editing.data[f.name] || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, [f.name]: e.target.value } })} className="w-full px-4 py-3 border border-slate-200 rounded-lg" data-testid={`field-${f.name}`} />
                  )}
                  {f.help && f.type !== "checkbox" && <div className="text-xs text-[color:var(--mia-muted)] mt-1">{f.help}</div>}
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={cancel} className="mia-btn mia-btn-outline">Cancel</button>
              <button onClick={save} disabled={saving} className="mia-btn mia-btn-secondary" data-testid="crud-save">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
